import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Mail, Copy, Check, Loader2, RefreshCw, ExternalLink, AlertCircle, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* Sent2ment palette — mirrors the rest of the app. */
const C = {
  cream: '#F5F1E8',
  paper: '#FBF8F1',
  midnight: '#0B1F3A',
  gold: '#C6A96B',
  text: '#0B1F3A',
  textSec: '#5A5A5A',
  border: 'rgba(198, 169, 107, 0.25)',
};

const fadeUp = {
  initial: { y: 12, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

/* Map order kind → human label. Mirrors backend `kind`. */
const KIND_LABEL = {
  letters_that_last: 'Letters That Last',
  letters_with_vault: 'Letters That Last + Memory Vault',
  memory_vault_digital: 'Memory Vault — digital',
  vault_with_addon: 'Memory Vault — physical + digital',
  other: 'Sent2ment order',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const formatMoney = (amount, currency) => {
  const cur = (currency || 'GBP').toUpperCase();
  const symbol = cur === 'GBP' ? '£' : `${cur} `;
  const n = Number(amount || 0).toFixed(2);
  return `${symbol}${n}`;
};

/* Small pill — used for status & order kind. */
const Pill = ({ children, tone = 'neutral', testid }) => {
  const tones = {
    gold: { bg: 'rgba(198,169,107,0.16)', fg: C.midnight, bd: 'rgba(198,169,107,0.5)' },
    neutral: { bg: 'rgba(11,31,58,0.06)', fg: C.midnight, bd: 'rgba(11,31,58,0.18)' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center text-[10px] tracking-[0.22em] uppercase font-semibold px-3 py-1"
      style={{ background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, borderRadius: '999px' }}
    >
      {children}
    </span>
  );
};

const CopyButton = ({ value, testid, label = 'Copy link' }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors"
      style={{ background: 'transparent', color: C.midnight, border: `1px solid ${C.border}`, borderRadius: '6px' }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : label}
    </button>
  );
};

/* ── Magic-link request form ───────────────────────────────────── */
const RequestLinkForm = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/orders/request-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      setSubmitted(true);
    } catch {
      toast.error('Something went wrong — please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        {...fadeUp}
        className="p-8 sm:p-10 max-w-xl mx-auto"
        style={{ background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '10px' }}
        data-testid="my-orders-link-sent"
      >
        <div className="flex justify-center mb-4">
          <Inbox size={28} strokeWidth={1.4} style={{ color: C.gold }} />
        </div>
        <h2
          className="text-2xl font-light text-center mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
        >
          Check your inbox.
        </h2>
        <p className="text-sm text-center leading-relaxed" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
          If you have orders with Sent2ment, we&rsquo;ve sent a sign-in link to{' '}
          <span style={{ color: C.midnight, fontStyle: 'italic' }}>{email.trim()}</span>. The link is yours alone and
          expires in 1 hour.
        </p>
        <p
          className="text-[12px] italic text-center mt-6"
          style={{ color: `${C.textSec}AA`, fontFamily: 'Georgia, serif' }}
        >
          Didn&rsquo;t receive it? Check spam, or{' '}
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="underline"
            style={{ color: C.gold }}
            data-testid="my-orders-try-again"
          >
            try again
          </button>
          .
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      {...fadeUp}
      onSubmit={onSubmit}
      className="p-8 sm:p-10 max-w-xl mx-auto"
      style={{ background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '10px' }}
      data-testid="my-orders-request-form"
    >
      <div className="flex justify-center mb-4">
        <Mail size={26} strokeWidth={1.4} style={{ color: C.gold }} />
      </div>
      <h2
        className="text-2xl sm:text-3xl font-light text-center mb-3"
        style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
        data-testid="my-orders-title"
      >
        Find your orders.
      </h2>
      <p
        className="text-sm text-center leading-relaxed mb-6 max-w-md mx-auto"
        style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}
      >
        Enter the email you used at checkout. We&rsquo;ll send a private sign-in link so you can return to your
        keepsakes, view your orders, and resend confirmations.
      </p>
      <label className="block mb-2 text-[11px] tracking-[0.22em] uppercase" style={{ color: C.gold }}>
        Email address
      </label>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        data-testid="my-orders-email-input"
        className="w-full px-4 py-3 text-[15px] outline-none mb-5"
        style={{
          background: '#FFFDF8',
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: '6px',
          fontFamily: 'Georgia, serif',
        }}
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="my-orders-send-link-btn"
        className="w-full py-3.5 text-[12px] tracking-[0.22em] uppercase font-semibold transition-opacity disabled:opacity-60"
        style={{ background: C.gold, color: C.midnight, border: 'none', borderRadius: '6px', cursor: loading ? 'wait' : 'pointer' }}
      >
        {loading ? 'Sending…' : 'Send my sign-in link'}
      </button>
      <p
        className="text-[11px] italic text-center mt-5"
        style={{ color: `${C.textSec}AA`, fontFamily: 'Georgia, serif' }}
      >
        No password needed. We&rsquo;ll only email a link if this address has orders.
      </p>
    </motion.form>
  );
};

/* ── Single order card ─────────────────────────────────────────── */
const OrderCard = ({ order, token, onResendDone }) => {
  const [resending, setResending] = useState(false);

  const onResend = async () => {
    setResending(true);
    try {
      const res = await fetch(`${API_BASE}/api/orders/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, order_id: order.order_id }),
      });
      if (res.ok) {
        toast.success('Confirmation email re-sent');
        onResendDone && onResendDone();
      } else if (res.status === 409) {
        toast.error('This order is still being prepared — try again shortly.');
      } else {
        toast.error('Could not resend just now. Please try again.');
      }
    } catch {
      toast.error('Could not resend just now. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const kindLabel = KIND_LABEL[order.kind] || KIND_LABEL.other;

  return (
    <motion.article
      {...fadeUp}
      className="p-6 sm:p-8 mb-6"
      style={{
        background: '#FFFFFF',
        border: `1px solid ${C.border}`,
        borderRadius: '10px',
        boxShadow: '0 6px 18px rgba(11,31,58,0.04), 0 1px 3px rgba(11,31,58,0.025)',
      }}
      data-testid={`order-card-${order.order_number}`}
    >
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p
            className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-1"
            style={{ color: C.gold }}
            data-testid={`order-${order.order_number}-number`}
          >
            Order #{order.order_number}
          </p>
          <p className="text-[12px]" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            {formatDate(order.created_at)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Pill tone="gold" testid={`order-${order.order_number}-kind`}>{kindLabel}</Pill>
          <Pill tone="neutral" testid={`order-${order.order_number}-status`}>{order.status || 'complete'}</Pill>
        </div>
      </div>

      {/* Items */}
      <div className="mb-5">
        {order.items.map((it, idx) => (
          <div
            key={`${order.order_id}-${idx}`}
            className="flex justify-between items-baseline py-2"
            style={{ borderBottom: idx < order.items.length - 1 ? `1px solid ${C.border}` : 'none' }}
            data-testid={`order-${order.order_number}-item-${idx}`}
          >
            <div className="pr-4">
              <p style={{ color: C.text, fontFamily: 'Georgia, serif' }} className="text-[14px]">
                {it.name} <span style={{ color: C.textSec }}>&times; {it.quantity}</span>
              </p>
              {it.options && it.options.length > 0 && (
                <p className="text-[11px] italic mt-0.5" style={{ color: C.textSec }}>
                  {it.options.filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <p className="text-[14px] whitespace-nowrap" style={{ color: C.text, fontFamily: 'Georgia, serif' }}>
              {formatMoney(Number(it.price) * Number(it.quantity || 1), order.currency)}
            </p>
          </div>
        ))}
        <div className="flex justify-end pt-3">
          <p className="text-[14px] font-semibold" style={{ color: C.midnight, fontFamily: 'Georgia, serif' }}>
            Total: {formatMoney(order.amount, order.currency)}
          </p>
        </div>
      </div>

      {/* Vault links (if any) */}
      {order.vaults && order.vaults.length > 0 && (
        <div
          className="p-5 mb-5"
          style={{ background: C.paper, border: `1px solid ${C.border}`, borderRadius: '8px' }}
          data-testid={`order-${order.order_number}-vaults`}
        >
          <p
            className="text-[10px] tracking-[0.28em] uppercase font-semibold mb-3"
            style={{ color: C.gold }}
          >
            Your private spaces
          </p>
          {order.vaults.map((v, idx) => (
            <div
              key={v.qr_code}
              className="py-3"
              style={{ borderBottom: idx < order.vaults.length - 1 ? `1px solid ${C.border}` : 'none' }}
              data-testid={`order-${order.order_number}-vault-${idx}`}
            >
              <p
                className="text-[15px] font-light mb-1"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
              >
                {v.label}
              </p>
              {(v.recipient_name || v.recipient_email) && (
                <p
                  className="text-[12px] italic mb-2"
                  style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}
                  data-testid={`order-${order.order_number}-vault-${idx}-recipient`}
                >
                  For {v.recipient_name || v.recipient_email}
                  {v.recipient_name && v.recipient_email ? ` (${v.recipient_email})` : ''}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <a
                  href={v.edit_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`order-${order.order_number}-vault-${idx}-edit`}
                  className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold"
                  style={{ background: C.gold, color: C.midnight, borderRadius: '6px' }}
                >
                  Open <ExternalLink size={12} />
                </a>
                <CopyButton
                  value={v.edit_url}
                  testid={`order-${order.order_number}-vault-${idx}-copy`}
                  label="Copy edit link"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LTL upload link */}
      {order.ltl_upload_url && (
        <div
          className="p-5 mb-5"
          style={{ background: C.paper, border: `1px solid ${C.border}`, borderRadius: '8px' }}
          data-testid={`order-${order.order_number}-ltl`}
        >
          <p className="text-[10px] tracking-[0.28em] uppercase font-semibold mb-2" style={{ color: C.gold }}>
            Your memory upload
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={order.ltl_upload_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`order-${order.order_number}-ltl-open`}
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold"
              style={{ background: C.gold, color: C.midnight, borderRadius: '6px' }}
            >
              Open <ExternalLink size={12} />
            </a>
            <CopyButton
              value={order.ltl_upload_url}
              testid={`order-${order.order_number}-ltl-copy`}
              label="Copy upload link"
            />
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex flex-wrap gap-3 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          data-testid={`order-${order.order_number}-resend`}
          className="inline-flex items-center gap-2 px-3 py-2 mt-3 text-[11px] tracking-[0.18em] uppercase transition-opacity disabled:opacity-60"
          style={{ background: 'transparent', color: C.midnight, border: `1px solid ${C.border}`, borderRadius: '6px' }}
        >
          {resending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {resending ? 'Sending…' : 'Resend confirmation email'}
        </button>
      </div>
    </motion.article>
  );
};

/* ── Authenticated dashboard ──────────────────────────────────── */
const Dashboard = ({ token }) => {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/orders/list?token=${encodeURIComponent(token)}`);
        if (!res.ok) {
          if (cancelled) return;
          setState({ status: 'expired', data: null, error: res.status });
          return;
        }
        const data = await res.json();
        if (!cancelled) setState({ status: 'ok', data, error: null });
      } catch (e) {
        if (!cancelled) setState({ status: 'expired', data: null, error: String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="flex flex-col items-center py-20" data-testid="my-orders-loading">
        <Loader2 size={28} className="animate-spin mb-3" style={{ color: C.gold }} />
        <p className="text-[12px] italic" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
          Opening your orders…
        </p>
      </div>
    );
  }

  if (state.status === 'expired') {
    return (
      <motion.div
        {...fadeUp}
        className="p-8 sm:p-10 max-w-xl mx-auto text-center"
        style={{ background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '10px' }}
        data-testid="my-orders-expired"
      >
        <div className="flex justify-center mb-4">
          <AlertCircle size={28} strokeWidth={1.4} style={{ color: C.gold }} />
        </div>
        <h2
          className="text-2xl font-light mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
        >
          This link has expired.
        </h2>
        <p className="text-sm leading-relaxed mb-6" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
          For your privacy, sign-in links are valid for 1 hour. Please request a fresh one and we&rsquo;ll send you
          a new link straight away.
        </p>
        <a
          href="/my-orders"
          data-testid="my-orders-request-new"
          className="inline-block px-6 py-3 text-[12px] tracking-[0.22em] uppercase font-semibold"
          style={{ background: C.gold, color: C.midnight, borderRadius: '6px' }}
        >
          Request a new link
        </a>
      </motion.div>
    );
  }

  const { data } = state;
  const orders = data?.orders || [];

  return (
    <div className="max-w-3xl mx-auto" data-testid="my-orders-dashboard">
      <motion.div {...fadeUp} className="text-center mb-8 sm:mb-10">
        <p className="text-[10px] tracking-[0.35em] uppercase font-semibold mb-2" style={{ color: C.gold }}>
          Your Sent2ment orders
        </p>
        <h1
          className="text-3xl sm:text-4xl font-light leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
          data-testid="my-orders-heading"
        >
          Welcome back, {data?.email}.
        </h1>
        <p className="text-sm mt-3" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
          Everything you&rsquo;ve created with us, in one calm place.
        </p>
      </motion.div>

      {orders.length === 0 ? (
        <motion.div
          {...fadeUp}
          className="p-8 text-center"
          style={{ background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '10px' }}
          data-testid="my-orders-empty"
        >
          <p style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            We don&rsquo;t see any orders for this email yet.
          </p>
        </motion.div>
      ) : (
        orders.map((o) => <OrderCard key={o.order_id} order={o} token={token} />)
      )}
    </div>
  );
};

/* ── Page ─────────────────────────────────────────────────────── */
const MyOrdersPage = () => {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token'), [location.search]);

  return (
    <main
      className="min-h-screen px-6 py-12 sm:py-16"
      style={{ background: C.cream, color: C.midnight }}
      data-testid="my-orders-page"
    >
      {token ? <Dashboard token={token} /> : <RequestLinkForm />}
    </main>
  );
};

export default MyOrdersPage;
